import React, { useEffect, useState } from 'react';
import { X, ShoppingBag } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, SlidersHorizontal, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productGradients = [
    'from-indigo-950/50 to-zinc-900',
    'from-zinc-800 to-zinc-900',
    'from-amber-950/30 to-zinc-900',
    'from-emerald-950/30 to-zinc-900',
    'from-rose-950/30 to-zinc-900',
    'from-violet-950/30 to-zinc-900',
];

export const ProductsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [quickView, setQuickView] = useState(null);
    const [sortBy, setSortBy] = useState('newest');
    const { addToCart } = useCart();

    const activeCategory = searchParams.get('category') || 'all';

    // Fixed categories - only T-Shirts and Hoodies
    const fixedCategories = [
        { slug: 'tshirts', name: 'T-Shirts' },
        { slug: 'hoodies', name: 'Hoodies' },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const productsRes = await axios.get(`${API}/products${activeCategory !== 'all' ? `?category=${activeCategory}` : ''}`);
                
                // Filter products to only show tshirts and hoodies
                let filteredProducts = productsRes.data.filter(p => 
                    p.category === 'tshirts' || p.category === 'hoodies'
                );
                
                if (sortBy === 'price-low') {
                    filteredProducts.sort((a, b) => a.price - b.price);
                } else if (sortBy === 'price-high') {
                    filteredProducts.sort((a, b) => b.price - a.price);
                }
                
                setProducts(filteredProducts);
                setCategories(fixedCategories);
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [activeCategory, sortBy]);

    const handleQuickAdd = async (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        const size = product.sizes?.[0] || null;
        await addToCart(product, 1, size);
        toast.success('Added to cart!');
    };

    return (
        <Layout>
            <div className="pt-32 md:pt-40 pb-16 md:pb-24" data-testid="products-page">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    {/* Header */}
                    <div className="mb-8 md:mb-12">
                        <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">
                            SHOP
                        </span>
                        <div className="flex justify-between items-end">
                            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl">
                                {activeCategory === 'all' ? 'ALL PRODUCTS' : activeCategory.toUpperCase()}
                            </h1>
                            <span className="text-muted-foreground text-sm hidden md:block">
                                {products.length} products
                            </span>
                        </div>
                    </div>

                    {/* Filters Bar */}
                    <div className="flex flex-wrap justify-between items-center gap-4 mb-8 pb-6 border-b border-border">
                        {/* Category filters */}
                        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                            <button
                                onClick={() => setSearchParams({})}
                                className={`whitespace-nowrap text-xs font-semibold tracking-[0.1em] uppercase px-4 py-2 border transition-all ${
                                    activeCategory === 'all'
                                        ? 'bg-lime text-black border-lime'
                                        : 'border-border text-muted-foreground hover:border-white hover:text-white'
                                }`}
                                data-testid="filter-all"
                            >
                                ALL
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.slug}
                                    onClick={() => setSearchParams({ category: cat.slug })}
                                    className={`whitespace-nowrap text-xs font-semibold tracking-[0.1em] uppercase px-4 py-2 border transition-all ${
                                        activeCategory === cat.slug
                                            ? 'bg-lime text-black border-lime'
                                            : 'border-border text-muted-foreground hover:border-white hover:text-white'
                                    }`}
                                    data-testid={`filter-${cat.slug}`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>

                        {/* Sort */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="flex items-center gap-2 text-xs font-medium tracking-[0.1em] uppercase border border-border px-4 py-2 hover:border-lime hover:text-lime transition-colors" data-testid="sort-dropdown">
                                    <SlidersHorizontal className="w-4 h-4" />
                                    SORT BY
                                    <ChevronDown className="w-3 h-3" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border">
                                <DropdownMenuItem onClick={() => setSortBy('newest')} className="text-xs uppercase tracking-wide cursor-pointer">
                                    Newest
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setSortBy('price-low')} className="text-xs uppercase tracking-wide cursor-pointer">
                                    Price: Low to High
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setSortBy('price-high')} className="text-xs uppercase tracking-wide cursor-pointer">
                                    Price: High to Low
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    {/* Products Grid */}
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="aspect-[3/4] bg-card animate-pulse" />
                            ))}
                        </div>
                    ) : products.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {products.map((product, index) => {
                                const gradient = productGradients[index % productGradients.length];
                                const discount = product.original_price 
                                    ? Math.round((1 - product.price / product.original_price) * 100) 
                                    : 0;

                                return (
                                    <motion.div
                                        key={product.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.03 }}
                                    >
                                        <Link
                                            to={`/products/${product.id}`}
                                            className="group block bg-card border border-border/50 hover:border-lime/50 transition-colors overflow-hidden"
                                            data-testid={`product-${product.id}`}
                                        >
                                            <div className="relative aspect-[3/4] overflow-hidden">
                                                <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
                                                <div className="absolute inset-0 flex items-center justify-center group-hover:scale-105 transition-transform duration-500">
                                                    {product.image_url ? (
                                                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <span className="text-6xl opacity-50">
                                                            {product.category === 'tshirts' ? '👕' : 
                                                             product.category === 'hoodies' ? '🧥' :
                                                             product.category === 'bottoms' ? '👖' :
                                                             product.category === 'shirts' ? '👔' : '🎒'}
                                                        </span>
                                                    )}
                                                </div>

                                                {product.badge && (
                                                    <div className={`absolute top-3 left-3 px-2 py-1 text-[9px] font-bold tracking-[0.1em] uppercase ${
                                                        product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                                                        product.badge === 'HOT' ? 'bg-lime text-black' : 'bg-white text-black'
                                                    }`}>
                                                        {product.badge}
                                                    </div>
                                                )}

                                                <button
                                                    onClick={(e) => handleQuickAdd(e, product)}
                                                    className="absolute bottom-0 left-0 right-0 bg-lime text-black py-3 text-xs font-bold tracking-[0.1em] uppercase opacity-0 group-hover:opacity-100 translate-y-full group-hover:translate-y-0 transition-all duration-300"
                                                    data-testid={`quick-add-${product.id}`}
                                                >
                                                    QUICK ADD
                                                </button>
                                            </div>

                                            <div className="p-3 md:p-4">
                                                <h3 className="text-sm font-medium leading-tight mb-2 truncate group-hover:text-lime transition-colors">
                                                    {product.name}
                                                </h3>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold">₹{product.price.toLocaleString()}</span>
                                                    {product.original_price && (
                                                        <>
                                                            <span className="text-sm text-muted-foreground line-through">
                                                                ₹{product.original_price.toLocaleString()}
                                                            </span>
                                                            <span className="text-xs font-bold text-lime">{discount}% OFF</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <p className="text-muted-foreground mb-4">No products found in this category.</p>
                            <Link to="/products" className="text-lime text-sm underline">View all products</Link>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};
